import type { AxiosError, AxiosResponse } from "axios"
import { isAxiosError } from "axios"

import { useAuthStore } from "@/store/auth"
import type {
  Fabric,
  FabricGroup,
  MiscellaneousPricesType,
  ModelType,
  RelatedModelType,
} from "@/types/api/published"
import type { AuthUri, Tokens, User } from "@/types/api/user"

import axios, { axiosUnauthenticated } from "./axios"

interface ApiError {
  error_code: string
  error_type: "client_error" | "server_error"
  error_detail?: string
  [key: string]: unknown
}

export type ParsedErrorType = ServerError | ClientError

export interface PaginatedResponse<T> {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

type ResponseError = ApiError | string

export class ServerError extends Error {
  constructor(
    error: Error,
    public statusCode: number,
    public error_type: string,
    public error_code: string,
    public error_detail: string,
    public field_errors: Record<string, unknown> = {}
  ) {
    super(error_detail)
    this.stack = error.stack
  }
}

export class ClientError extends Error {
  error_type: string
  constructor(
    error: Error,
    public error_detail: string,
    public field_errors: Record<string, unknown> = {}
  ) {
    super(error_detail)
    this.stack = error.stack
    this.error_type = "client_error"
  }
}

export function handleError(error: Error | AxiosError<ResponseError>): ServerError | ClientError {
  if (isAxiosError(error)) {
    if (!error.response) {
      return new ServerError(error, 500, "server_error", "server_error", error.message)
    }

    if (typeof error.response?.data === "string") {
      return new ServerError(
        error,
        error.response.status,
        "server_error",
        "server_error",
        error.response.data
      )
    }

    const data = error.response?.data as ApiError

    const { error_type, error_code, error_detail, ...field_errors } = data

    return new ServerError(
      error,
      error.response.status,
      error_type,
      error_code,
      error_detail || "",
      field_errors
    )
  } else {
    return new ClientError(error, error.message)
  }
}

export async function getAccessToken(refresh_token: string) {
  return axiosUnauthenticated
    .post("/user/token/refresh/", { refresh: refresh_token })
    .then((res: AxiosResponse<Tokens>) => res)
    .catch((error) => {
      const errorObject = handleError(error)
      if (errorObject instanceof ServerError && errorObject.statusCode === 401) {
        useAuthStore.getState().reset()
        window.location.href = "/auth/login"
      }
      throw errorObject
    })
}

export async function getCurrentUser(): Promise<User> {
  return axios
    .get("/user/me/current/")
    .then((res: AxiosResponse<User>) => {
      useAuthStore.getState().setUser(res.data)
      return res.data
    })
    .catch((error) => {
      const errorObject = handleError(error)
      if (errorObject instanceof ServerError && errorObject.statusCode === 401) {
        useAuthStore.getState().reset()
        window.location.href = "/auth/login"
      }
      throw errorObject
    })
}

export async function updateCurrentUserCurrency(data: Partial<User>): Promise<User> {
  return axios
    .patch("/user/me/current/", data)
    .then((res: AxiosResponse<User>) => {
      useAuthStore.getState().setUser(res.data)
      return res.data
    })
    .catch((err) => {
      throw handleError(err)
    })
}

export async function getMicrosoftLogin(signal: AbortSignal | undefined, host?: string) {
  return axiosUnauthenticated
    .get("/user/social/sat-azure-tenant/auth-uri/", { signal, params: { host } })
    .then((res: AxiosResponse<AuthUri>) => res.data)
    .catch((err) => {
      throw handleError(err)
    })
}

export async function getAuth0Login(
  signal: AbortSignal | undefined,
  host?: string,
  email?: string,
  signup?: boolean
) {
  return axiosUnauthenticated
    .get("/user/social/auth0-prijslijst/auth-uri/", {
      signal,
      params: { host, email, screen_hint: signup ? "signup" : undefined },
    })
    .then((res: AxiosResponse<AuthUri>) => res.data)
    .catch((err) => {
      throw handleError(err)
    })
}

export async function callBack(provider: string = "sat-azure-tenant", code: string, host?: string) {
  return axiosUnauthenticated
    .get(`/user/social/${provider}/callback/`, {
      params: { code, host },
    })
    .then((res: AxiosResponse<Tokens>) => res.data)
    .catch((err) => {
      throw handleError(err)
    })
}

export async function getModel(signal: AbortSignal | undefined, model_nr: string = "") {
  return axios
    .get(`/prijslijst/published-models/${model_nr}/`, { signal })
    .then((res: AxiosResponse<ModelType>) => res.data)
    .catch((err) => {
      throw handleError(err)
    })
}

export async function getRelatedModels(signal: AbortSignal | undefined, model_nr: string = "") {
  return axios
    .get(`/prijslijst/published-models/${model_nr}/related-models/`, { signal })
    .then((res: AxiosResponse<RelatedModelType[]>) => res.data)
    .catch((err) => {
      throw handleError(err)
    })
}

export async function getFabrics(
  signal: AbortSignal | undefined,
  params?: { [key: string]: unknown }
) {
  return axios
    .get("/prijslijst/fabrics/", { signal, params })
    .then((res: AxiosResponse<PaginatedResponse<Fabric>>) => res.data)
    .catch((err) => {
      throw handleError(err)
    })
}

export async function getFabricGroups(
  signal: AbortSignal | undefined,
  params?: { [key: string]: unknown }
) {
  return axios
    .get("/prijslijst/fabric-groups/", { signal, params })
    .then((res: AxiosResponse<PaginatedResponse<FabricGroup>>) => res.data)
    .catch((err) => {
      throw handleError(err)
    })
}

export async function getMiscellaneousPrices(signal: AbortSignal | undefined, page: string) {
  return axios
    .get(`/prijslijst/miscellaneous-prices/`, { signal, params: { page } })
    .then((res: AxiosResponse<PaginatedResponse<MiscellaneousPricesType>>) => res.data)
    .catch((err) => {
      throw handleError(err)
    })
}
